import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import analyticsService from "../../services/analyticsService";
import "./AttributionAnalytics.css";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AttributionAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ dateFrom: "", dateTo: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await analyticsService.getAttributionAnalytics(dateRange);
      if (response.success) {
        setData(response.data);
      } else {
        setError(response.error || "Failed to fetch attribution analytics");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className="attribution-analytics">
        <h1>Marketing Attribution</h1>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading attribution data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="attribution-analytics">
        <h1>Marketing Attribution</h1>
        <div className="error-container">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
          <button onClick={fetchData} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  const { summary, campaigns, acquisition } = data;

  // Chart data
  const sourceChartData = {
    labels: campaigns.utmSourceBreakdown.map((s) => s._id || "Unknown"),
    datasets: [{
      label: "Events",
      data: campaigns.utmSourceBreakdown.map((s) => s.count),
      backgroundColor: ["#2196f3", "#4caf50", "#ff9800", "#9c27b0", "#f44336"],
    }],
  };

  const acquisitionChartData = {
    labels: acquisition.acquisitionSourceBreakdown.map((a) => a._id || "Unknown"),
    datasets: [{
      data: acquisition.acquisitionSourceBreakdown.map((a) => a.count),
      backgroundColor: ["#667eea", "#764ba2", "#f093fb", "#4facfe", "#43e97b"],
    }],
  };

  return (
    <div className="attribution-analytics">
      <div className="analytics-header">
        <h1>Marketing Attribution</h1>
        <button onClick={fetchData} className="refresh-button">
          <span className="material-symbols-outlined">refresh</span>
          Refresh
        </button>
      </div>

      {/* Date Range Filter */}
      <div className="filters-section">
        <input
          type="date"
          value={dateRange.dateFrom}
          onChange={(e) => setDateRange({ ...dateRange, dateFrom: e.target.value })}
          className="date-input"
        />
        <input
          type="date"
          value={dateRange.dateTo}
          onChange={(e) => setDateRange({ ...dateRange, dateTo: e.target.value })}
          className="date-input"
        />
        <button onClick={applyFilters} className="apply-button">Apply</button>
      </div>

      {/* Summary Cards */}
      <div className="analytics-grid">
        <div className="analytics-card">
          <div className="card-icon total">
            <span className="material-symbols-outlined">stacked_line_chart</span>
          </div>
          <div className="card-content">
            <h3>Total Events</h3>
            <p className="metric-value">{summary.totalEvents.toLocaleString()}</p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon users">
            <span className="material-symbols-outlined">group</span>
          </div>
          <div className="card-content">
            <h3>Unique Users</h3>
            <p className="metric-value">{summary.uniqueUsers.toLocaleString()}</p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon facebook">
            <span className="material-symbols-outlined">thumb_up</span>
          </div>
          <div className="card-content">
            <h3>Facebook Clicks</h3>
            <p className="metric-value">{summary.facebookClicks.toLocaleString()}</p>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon google">
            <span className="material-symbols-outlined">search</span>
          </div>
          <div className="card-content">
            <h3>Google Clicks</h3>
            <p className="metric-value">{summary.googleClicks.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3>UTM Source Distribution</h3>
          <div className="chart-container">
            <Bar data={sourceChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>

        <div className="chart-card">
          <h3>Acquisition Source Breakdown</h3>
          <div className="chart-container pie-chart">
            <Pie data={acquisitionChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      {/* Campaign Performance Table */}
      <div className="table-card">
        <h3>Campaign Performance</h3>
        <table className="campaigns-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Source</th>
              <th>Medium</th>
              <th>Events</th>
              <th>Unique Users</th>
              <th>Devices</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.campaignPerformance.map((campaign, index) => (
              <tr key={index}>
                <td>{campaign._id.campaign || "N/A"}</td>
                <td>{campaign._id.source || "N/A"}</td>
                <td>{campaign._id.medium || "N/A"}</td>
                <td>{campaign.totalEvents.toLocaleString()}</td>
                <td>{campaign.uniqueUsers.toLocaleString()}</td>
                <td>{campaign.uniqueDevices.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Facebook & Google Tracking */}
      <div className="tracking-grid">
        <div className="table-card">
          <h3>Facebook Campaigns (fbclid)</h3>
          <table className="tracking-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Clicks</th>
                <th>Unique Users</th>
              </tr>
            </thead>
            <tbody>
              {acquisition.facebookTracking.map((fb, index) => (
                <tr key={index}>
                  <td>{fb._id || "Unknown"}</td>
                  <td>{fb.clicks.toLocaleString()}</td>
                  <td>{fb.uniqueUsers.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="table-card">
          <h3>Google Campaigns (gclid)</h3>
          <table className="tracking-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Clicks</th>
                <th>Unique Users</th>
              </tr>
            </thead>
            <tbody>
              {acquisition.googleTracking.map((google, index) => (
                <tr key={index}>
                  <td>{google._id || "Unknown"}</td>
                  <td>{google.clicks.toLocaleString()}</td>
                  <td>{google.uniqueUsers.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttributionAnalytics;
